import React from 'react';
import { Head, Link, useForm } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Save, Package, GitBranch, Merge, AlertCircle } from 'lucide-react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';

interface Document {
  id: number;
  name: string;
}

interface Job {
  id: number;
  name: string;
}

interface ChunkCollectionNewProps {
  document: Document;
  parse_jobs: Job[];
  extract_jobs: Job[];
  errors: Record<string, string[]>;
}

export default function ChunkCollectionNew({ 
  document, 
  parse_jobs, 
  extract_jobs, 
  errors = {} 
}: ChunkCollectionNewProps) {
  const { data, setData, post, processing, errors: formErrors } = useForm({
    name: '',
    parse_job_id: '',
    extract_job_id: '',
  });

  const allErrors = { ...errors, ...formErrors };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert "none" back to empty string for the backend
    const submitData = {
      ...data,
      extract_job_id: data.extract_job_id === 'none' ? '' : data.extract_job_id
    };
    
    post(`/documents/${document.id}/chunk_collections`, {
      data: { chunk_collection: submitData },
      onSuccess: () => {
        // Handle success - Inertia will handle the redirect
      },
    });
  };

  const hasAnyErrors = Object.keys(allErrors).some(key => allErrors[key].length > 0);

  return (
    <>
      <Head title={`Create Chunk Collection - ${document.name}`} />
      
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: 'New Chunk Collection', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex items-center gap-4">
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Document
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">
              Create Chunk Collection for {document.name}
            </h1>
          </div>

          {/* Error Summary */}
          {hasAnyErrors && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">
                  {Object.keys(allErrors).reduce((count, key) => count + allErrors[key].length, 0)} error(s) found:
                </div>
                <ul className="list-disc list-inside space-y-1">
                  {Object.entries(allErrors).map(([field, fieldErrors]) =>
                    fieldErrors.map((error, index) => (
                      <li key={`${field}-${index}`}>{error}</li>
                    ))
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Chunk Collection Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Collection Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    Collection Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={data.name}
                    onChange={(e) => setData('name', e.target.value)}
                    className={allErrors.name ? 'border-red-500' : ''}
                    placeholder="Enter collection name"
                    required
                  />
                  {allErrors.name && (
                    <div className="text-sm text-red-600">
                      {allErrors.name.join(', ')}
                    </div>
                  )}
                </div>

                {/* Parse Job Selection */}
                <div className="space-y-2">
                  <Label htmlFor="parse_job_id" className="text-sm font-medium flex items-center gap-2">
                    <GitBranch className="h-4 w-4" />
                    Select Parse Job *
                  </Label>
                  <Select 
                    value={data.parse_job_id} 
                    onValueChange={(value) => setData('parse_job_id', value)}
                    required
                  >
                    <SelectTrigger className={allErrors.parse_job_id ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Choose a parse job" />
                    </SelectTrigger>
                    <SelectContent>
                      {parse_jobs.sort((a, b) => a.name.localeCompare(b.name)).map((job) => (
                        <SelectItem key={job.id} value={job.id.toString()}>
                          {job.name} (ID: {job.id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {allErrors.parse_job_id && (
                    <div className="text-sm text-red-600">
                      {allErrors.parse_job_id.join(', ')}
                    </div>
                  )}
                </div>

                {/* Extract Job Selection */}
                <div className="space-y-2">
                  <Label htmlFor="extract_job_id" className="text-sm font-medium flex items-center gap-2">
                    <Merge className="h-4 w-4" />
                    Select Extract Job (Optional)
                  </Label>
                  <Select 
                    value={data.extract_job_id} 
                    onValueChange={(value) => setData('extract_job_id', value)}
                  >
                    <SelectTrigger className={allErrors.extract_job_id ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Choose an extract job (if applicable)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Extract Job</SelectItem>
                      {extract_jobs.sort((a, b) => a.name.localeCompare(b.name)).map((job) => (
                        <SelectItem key={job.id} value={job.id.toString()}>
                          {job.name} (ID: {job.id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-gray-600">
                    Extract jobs are optional and used for metadata extraction
                  </div>
                  {allErrors.extract_job_id && (
                    <div className="text-sm text-red-600">
                      {allErrors.extract_job_id.join(', ')}
                    </div>
                  )}
                </div>

                {/* Info Card */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Package className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-2">About Chunk Collections</p>
                        <p>
                          Chunk collections organize document content based on parse jobs and optional extract jobs. 
                          Parse jobs define how the document is split into chunks, while extract jobs add metadata 
                          and structured data to those chunks.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Submit Button */}
                <div className="flex justify-center pt-6">
                  <Button 
                    type="submit" 
                    disabled={processing}
                    size="lg"
                    className="px-8 bg-purple-600 hover:bg-purple-700"
                  >
                    {processing ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Creating Collection...
                      </div>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Create Chunk Collection
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}